/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-param-reassign */
// 9fbef606107a605d69c0edbcd8029e5d
import isEmpty from 'lodash/isEmpty';
import ExecutionEnvironment from 'exenv';
import { routerPush } from '@tcp/core/src/utils';
import { breakpoints } from '@tcp/core/styles/themes/TCP/mediaQuery';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  renderLabel,
  getLabelFromCopyOrFromTranslation,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { slpSubLabels, SLP_SUB_LABELS } from '@tcp/core/src/utils/labels/browse/SLP.labels';
import {
  getTopSearchTextQuery,
  getAutosuggestTextWithCorrectCase,
  checkForKeyPressed,
} from '../utils/NewSearchBar.util';
import {
  newSearchBarLabels,
  SEARCH_BAR_LABELS,
  lblWhatLookingFor,
  lblSearchProductMatches,
} from '../labels/NewSearchBar.labels';
import CONSTANTS from '../container/NewSearchBar.constants';
import {
  updateLocalStorageData,
  isReturningUserWebSearchEnhancement,
} from '../container/userRecentStore';

import { SearchIcon, CloseIcon } from '../components/Icons';
import SearchSuggestionLayout from '../components/SearchSuggestionLayout.view';
import {
  Section,
  StyledLabel,
  SearchInput,
  DarkOverlay,
  SearchInputGhost,
  DropdownContainer,
  CloseIconContainer,
  SearchBarContainer,
  SearchInputWrapper,
  SearchIconContainer,
  DropdownCloseIconContainer,
} from '../styles/NewSearchBar.style';

const iconIds = [
  '#wishlistIcon svg',
  '#Store-icon-svg svg',
  '#non-login-user svg',
  '#accountDrawer svg',
];

export const applyStyles = (icon, shouldHide) => {
  icon.style.transform = shouldHide ? 'translateX(-20px)' : 'translateX(0px)';
  icon.style.opacity = shouldHide ? '0' : '1';
  icon.style.transition = 'transform 0.6s ease, opacity 0.6s ease';
};

const updateIconVisibility = (shouldHide) => {
  iconIds.forEach((selector) => {
    const icon = document.querySelector(selector);
    if (icon) {
      applyStyles(icon, shouldHide);
    }
  });
};

const redirectToProductUrl = (
  productUrl,
  searchHeaderTerm,
  setSearchTerm,
  setIsDropdownOpen,
  toggleExpand,
  unsetPLPBreadCrumbAction
) => {
  setSearchTerm('');
  setIsDropdownOpen(false);
  toggleExpand();
  unsetPLPBreadCrumbAction({ breadCrumbTrail: null });

  if (productUrl) {
    if (productUrl.indexOf('/p/') !== -1) {
      routerPush(
        `/p?pid=${productUrl.split('/p/')[1]}&imageSearch=${true}&searchType=${searchHeaderTerm}`,
        `${productUrl}`,
        {
          shallow: false,
        }
      );
    } else {
      routerPush(`/b?bid=${productUrl.split('/b/')[1]}&imageSearch=${true}`, `${productUrl}`, {
        shallow: false,
      });
    }
  }
};

export const setDataInLocalStorage = (searchText, url) => {
  updateLocalStorageData(searchText, url);
};

const NewSearchBar = (props) => {
  const {
    startSearch,
    searchResults,
    closeNavDrawer,
    trendingSearch,
    trendingResults,
    cmsTrendingSearches,
    trackAnalyticsClick,
    toggleSearchResults,
    startTrendingSearch,
    trendingSearchProducts,
    cmsTrendingSearchResults,
    unsetPLPBreadCrumbAction,
    getTrendingSearchProducts,
    isSearchBarAutosuggestProductsEnabled,
  } = props;

  const searchBarLabels = getLabelFromCopyOrFromTranslation(SEARCH_BAR_LABELS, newSearchBarLabels);
  const slpLabels = getLabelFromCopyOrFromTranslation(SLP_SUB_LABELS, slpSubLabels);
  const isReturningUser = isReturningUserWebSearchEnhancement();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const lastKeyDownRef = useRef(null);
  const searchInputGhost = useRef(null);
  const isMobileView =
    ExecutionEnvironment.canUseDOM && document.body.offsetWidth < breakpoints.values.sm;

  const hideUnrelatedIcon = isMobileView && isDropdownOpen;
  const rightIconWidth =
    ExecutionEnvironment.canUseDOM && document.getElementsByClassName('content-wrapper-right')[0];

  const topSearchTextQuery = getTopSearchTextQuery(trendingResults);

  const handleClickOutside = useCallback((event) => {
    setTimeout(() => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchTerm('');
        if (isMobileView) {
          setTimeout(() => setIsDropdownOpen(false), 0);
        } else {
          setIsDropdownOpen(false);
        }
        inputRef.current.blur();
        if (isMobileView) {
          setIsExpanded(false);
        }
      }
    }, 0);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    const applyFixedPosition = (scrollY) => {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    };

    const resetPosition = () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    };

    const dropDownContainer = document.getElementById('dropdown-container');
    if (dropDownContainer) {
      if (isDropdownOpen) {
        dropDownContainer.classList.add('dropdown-open');
        const { scrollY } = window;

        if (isMobileView) {
          setTimeout(() => applyFixedPosition(scrollY), 300);
        } else {
          applyFixedPosition(scrollY);
        }
      } else {
        resetPosition();
        dropDownContainer.classList.remove('dropdown-open');
      }
    }
    const focusableSelectors = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
    ];
    const focusableEls = dropdownRef.current.querySelectorAll(focusableSelectors.join(','));
    focusableEls.forEach((el) => el.setAttribute('tabindex', '-1'));
  }, [isDropdownOpen]);

  useEffect(() => {
    if (hideUnrelatedIcon && rightIconWidth && rightIconWidth?.style) {
      updateIconVisibility(true);
      rightIconWidth.style.width = '40px';
    } else if (!isDropdownOpen) {
      updateIconVisibility(false);
      if (rightIconWidth) {
        rightIconWidth.style.width = 'auto';
      }
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    startTrendingSearch({ webSearchEnhancement: true });
  }, [trendingSearch]);

  useEffect(() => {
    if (
      isEmpty(trendingSearchProducts) &&
      topSearchTextQuery &&
      isSearchBarAutosuggestProductsEnabled
    ) {
      getTrendingSearchProducts(topSearchTextQuery);
    }
  }, [topSearchTextQuery]);

  const getAutoSuggestionPlaceholderText = () => {
    const { autosuggestList } = searchResults;

    let searchText = inputRef && inputRef.current ? inputRef.current.value : '';
    searchText = searchText.replace(/ %|% |%/g, ' ').trim();
    let matchingText;
    if (searchText && autosuggestList) {
      const suggestionsList = autosuggestList && autosuggestList[0]?.suggestions;
      matchingText =
        suggestionsList &&
        suggestionsList.filter((suggestionItem) => {
          const suggestionText = suggestionItem?.text?.toLowerCase();
          return suggestionText.startsWith(searchText?.toLowerCase());
        });
      matchingText = matchingText && matchingText[0]?.text?.toLowerCase();
      matchingText = getAutosuggestTextWithCorrectCase(searchText, matchingText);
      if (matchingText && searchInputGhost && searchInputGhost.current) {
        searchInputGhost.current.value = matchingText;
      } else if (searchInputGhost && searchInputGhost.current) {
        searchInputGhost.current.value = '';
      }
      return matchingText;
    }
    return '';
  };

  const handleInputChange = () => {
    const searchText = inputRef && inputRef.current ? inputRef.current.value : '';
    setSearchTerm(searchText);
    setIsDropdownOpen(true);
    if (!searchText && searchInputGhost && searchInputGhost.current) {
      searchInputGhost.current.value = '';
    }
    if (searchText.length <= 0) {
      toggleSearchResults(false);
    } else {
      const payload = {
        searchText: searchText.replace(/ %|% |%/g, ' ').trim(),
        fromNewSearchBar: true,
      };
      startSearch(payload);
    }
    let autosuggestionFirstMatchText = getAutoSuggestionPlaceholderText();
    if (autosuggestionFirstMatchText) {
      document.addEventListener('keydown', (event) => {
        const isRightArrowKeyPressed = checkForKeyPressed(event);
        autosuggestionFirstMatchText = getAutoSuggestionPlaceholderText();
        if (isRightArrowKeyPressed && autosuggestionFirstMatchText) {
          document.getElementById('search-input').value = autosuggestionFirstMatchText;
        }
      });
    }
  };

  const clearSearch = (e) => {
    e?.preventDefault();
    setTimeout(() => {
      const payload = {
        searchText: '',
      };
      startSearch(payload);
      setSearchTerm('');
      if (searchInputGhost && searchInputGhost.current) {
        searchInputGhost.current.value = '';
      }
      inputRef.current.focus();
    }, 0);
  };

  const closeDropdown = (e) => {
    e?.preventDefault();
    setTimeout(() => {
      toggleSearchResults(false);
      setSearchTerm('');
      setIsDropdownOpen(false);
      inputRef.current.blur();
      if (searchInputGhost && searchInputGhost.current) {
        searchInputGhost.current.value = '';
      }
      if (isMobileView) setIsExpanded(false);
    }, 0);
  };
  const handleBlur = (e) => {
    const isTabKey = lastKeyDownRef.current === 'Tab';
    const focusLeavingDropdown = !e.currentTarget.contains(e.relatedTarget);
    console.log('isTabKey', isTabKey, focusLeavingDropdown);
    if (isTabKey && focusLeavingDropdown) {
      closeDropdown(e);
    }
    lastKeyDownRef.current = null;
  };
  const toggleExpand = () => {
    if (isMobileView) {
      if (isExpanded && document.activeElement === inputRef.current) return;
      setIsExpanded(!isExpanded);
      setIsDropdownOpen(!isExpanded);
      if (!isExpanded) inputRef.current.focus();
    }
  };

  const openSearhBar = (e) => {
    e?.preventDefault();
    closeNavDrawer();
    trackAnalyticsClick(
      { nav_cd171: 'header-search-started', customEvents: ['event82'] },
      { name: 'header-search-started' }
    );
    toggleSearchResults(false);
    inputRef.current.focus();
    setIsDropdownOpen(true);
    toggleExpand();
  };

  const redirectToSearchPage = (searchText) => {
    setIsDropdownOpen(false);
    toggleSearchResults(false);
    routerPush(`/search?searchQuery=${searchText}&searchType=keyword`, `/search/${searchText}`, {
      shallow: true,
    });
  };

  const startInitiateSearch = () => {
    setTimeout(() => {
      let searchText = inputRef && inputRef.current ? inputRef.current.value : '';
      searchText = searchText.replace(/ %|% |%/g, ' ').trim();

      if (searchText) {
        setDataInLocalStorage(searchText);
        setSearchTerm('');
        if (searchInputGhost && searchInputGhost.current) {
          searchInputGhost.current.value = '';
        }
        setIsDropdownOpen(false);
        inputRef.current.blur();
        toggleExpand();
        redirectToSearchPage(searchText);
      }
    }, 0);
  };

  const initiateSearch = (e) => {
    e?.preventDefault();
    startInitiateSearch();
  };

  const initiateSearchBySubmit = (e) => {
    e?.preventDefault();
    startInitiateSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      lastKeyDownRef.current = 'Tab';
    }
    lastKeyDownRef.current = e.key;
    if (e.key === 'Enter' && document.activeElement === inputRef.current && searchTerm !== '') {
      initiateSearch(e);
    }
  };

  const onSearchIconClick = (e) => {
    e?.preventDefault();
    setTimeout(() => {
      if (!isDropdownOpen) {
        openSearhBar(e);
      }
      if (isDropdownOpen && inputRef.current.value !== '') {
        initiateSearch(e);
      }
      if (isDropdownOpen && inputRef.current.value === '') {
        inputRef.current.focus();
      }
    }, 0);
  };

  const searchHeaderTerm = inputRef?.current?.value
    ? renderLabel(searchBarLabels[lblSearchProductMatches], lblSearchProductMatches)
    : renderLabel(slpLabels?.lbl_featured_products, 'lbl_featured_products');

  const redirectToSuggestedUrl = (searchText, url) => {
    if (searchText) {
      setDataInLocalStorage(searchText, url);
    }
    setSearchTerm('');
    setIsDropdownOpen(false);
    toggleExpand();
    if (url) {
      routerPush(
        `/c?searchType=${CONSTANTS.ANALYTICS_TYPEAHEAD_CATEGORY}&cid=${url.split('/c/')[1]}`,
        `${url}`,
        { shallow: false }
      );
    } else {
      routerPush(
        `/search?searchQuery=${searchText}&searchType=i am looking for products`,
        `/search/${searchText}`,
        {
          shallow: true,
        }
      );
    }
  };
  const commonProps = {
    ...props,
    redirectToProductUrl: (productUrl) =>
      redirectToProductUrl(
        productUrl,
        searchHeaderTerm?.toLowerCase(),
        setSearchTerm,
        setIsDropdownOpen,
        toggleExpand,
        unsetPLPBreadCrumbAction
      ),
    redirectToSuggestedUrl,
    isReturningUser,
    searchResults,
    cmsTrendingSearchResults,
    isSearchBarAutosuggestProductsEnabled,
    searchInput: inputRef,
    cmsTrendingSearches,
    hasSearchTerm: searchTerm.length,
  };

  const middleBanner = isMobileView && document.querySelector('.header-middle-login-section');
  let middleBannerVal;
  let overallHeight;
  if (isMobileView && middleBanner) {
    middleBannerVal = middleBanner.getBoundingClientRect().bottom;
    overallHeight = window.innerHeight - middleBannerVal;
  }

  return (
    <SearchBarContainer className="search-bar-container" ref={searchRef}>
      <SearchInputWrapper
        id="searchBarInputForm"
        className="search-bar-wrapper"
        isExpanded={isExpanded}
        noValidate
        onSubmit={initiateSearchBySubmit}
      >
        <SearchIconContainer
          className="svg-search-icon"
          onClick={onSearchIconClick}
          isExpanded={isExpanded}
        >
          <SearchIcon />
        </SearchIconContainer>
        <StyledLabel htmlFor="search-input" className="visuallyhidden input-box">
          <SearchInputGhost
            id="search-input-ghost"
            className="search-bar-input-ghost"
            ref={searchInputGhost}
            type="text"
            autoComplete="off"
            onFocus={() => setIsDropdownOpen(true)}
          />

          <SearchInput
            id="search-input"
            className="search-bar-input"
            ref={inputRef}
            type="text"
            autoComplete="off"
            aria-label="Search Bar Input Group press the tab key to enter the search field"
            placeholder={renderLabel(searchBarLabels[lblWhatLookingFor], lblWhatLookingFor)}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => {
              setTimeout(() => {
                closeNavDrawer();
                toggleExpand();
                setIsDropdownOpen(true);
              }, 0);
            }}
            isVisible={isExpanded}
          />
          <p>{renderLabel(searchBarLabels[lblWhatLookingFor], lblWhatLookingFor)}</p>
        </StyledLabel>
        {searchTerm && (
          <CloseIconContainer
            isVisible={isDropdownOpen}
            onClick={clearSearch}
            className="svg-close-icon"
          >
            <CloseIcon />
          </CloseIconContainer>
        )}
      </SearchInputWrapper>
      <DropdownContainer
        ref={dropdownRef}
        isVisible={isDropdownOpen}
        isOpen={isDropdownOpen}
        id="dropdown-container"
        heightVal={overallHeight}
      >
        <DropdownCloseIconContainer onClick={(e) => closeDropdown(e)}>
          <CloseIcon />
        </DropdownCloseIconContainer>
        <Section className={`${isReturningUser ? 'returning-user' : ''}`}>
          <SearchSuggestionLayout
            {...commonProps}
            onCloseClick={(e) => closeDropdown(e)}
            closeSearchBar={(e) => closeDropdown(e)}
          />
        </Section>
      </DropdownContainer>
      <DarkOverlay isVisible={isDropdownOpen} onClick={(e) => closeDropdown(e)} />
    </SearchBarContainer>
  );
};

export default NewSearchBar;
export { NewSearchBar as NewSearchBarVanilla };
